import React from 'react';
import { ProgressBar } from '../ui/ProgressBar';
import { useActivityPlan } from '../../contexts/ActivityPlanContext';
import { activityById } from '../../data/activityCatalog';
import { actualForPeriod, paceCopy, paceOf, percentOf, periodLabel, targetForPeriod } from '../../utils/activityProgress';
import type { PlanPeriod } from '../../types/activity';
interface PeriodProgressPanelProps {
  period: PlanPeriod;
  showCoaching?: boolean;
}
export function PeriodProgressPanel({
  period,
  showCoaching = false
}: PeriodProgressPanelProps) {
  const {
    activeGoals,
    logs
  } = useActivityPlan();
  const rows = activeGoals.map(goal => {
    const definition = activityById[goal.category];
    const target = targetForPeriod(goal, period);
    const actual = actualForPeriod(logs, goal.category, period);
    return {
      goal,
      definition,
      target,
      actual,
      percent: percentOf(actual, target),
      pace: paceOf(actual, target)
    };
  });
  const overall = rows.length ? Math.round(rows.reduce((total, row) => total + row.percent, 0) / rows.length) : 0;
  return <section aria-labelledby={`period-progress-${period}`} className="rounded-2xl border border-navy-100 bg-white shadow-card" data-id="element-134" data-mp="15lod9x7ysked">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-navy-100 px-5 py-4" data-id="element-135" data-mp="1ell527pnhzdn">
        <div data-id="element-136" data-mp="1jkxqco0viye4">
          <h2 id={`period-progress-${period}`} className="text-sm font-semibold text-navy-900" data-id="element-137" data-mp="1jl62gdp69zo9">
            {periodLabel[period]}
          </h2>
          <p className="mt-0.5 text-xs text-navy-500" data-id="element-138" data-mp="1tapottc3gbnv">
            Actual versus your own targets
          </p>
        </div>
        <div className="text-right" data-id="element-139" data-mp="1y9txmiv3w7do">
          <p className="text-2xl font-bold tabular-nums text-navy-900" data-id="element-140" data-mp="1y8r5y8pym0wp">
            {overall}%
          </p>
          <p className="text-xs text-navy-500" data-id="element-141" data-mp="2292rrou2679r">of plan completed</p>
        </div>
      </header>

      {rows.length === 0 ? <p className="px-5 py-8 text-center text-sm text-navy-500" data-id="element-142" data-mp="77ptv21mox7b">
          No activity goals yet. Build your perfect week to start tracking.
        </p> : null}

      <ul className="divide-y divide-navy-100" data-id="element-143" data-mp="34b2xgc5iwf3">
        {rows.map(row => {
        const Icon = row.definition.icon;
        return <li key={row.goal.category} className="px-5 py-4" data-id="element-144" data-mp="c6u29qgqaqwl">
              <div className="flex items-center justify-between gap-3" data-id="element-145" data-mp="c595du6xyvvk">
                <div className="flex min-w-0 items-center gap-2" data-id="element-146" data-mp="quf6xot94y6m">
                  <Icon className="h-4 w-4 shrink-0 text-navy-400" aria-hidden="true" data-id="element-147" data-mp="lxoo4tq2bohi" />
                  <p className="truncate text-sm font-semibold text-navy-900" data-id="element-148" data-mp="zukatoz8leg1">
                    {row.definition.shortLabel}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3" data-id="element-149" data-mp="31ot3j9jc8umt">
                  <p className="text-sm font-semibold tabular-nums text-navy-700" data-id="element-150" data-mp="2wpgeci4zvbh2">
                    {row.actual}
                    <span className="text-navy-400" data-id="element-151" data-mp="1c7xh18nao4sg"> / {row.target}</span>
                  </p>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${paceCopy[row.pace].classes}`} data-id="element-152" data-mp="1h68urugpmzfy">
                    {paceCopy[row.pace].label}
                  </span>
                </div>
              </div>
              <ProgressBar className="mt-2" value={row.actual} max={row.target} size="sm" tone={row.pace === 'ahead' ? 'success' : row.definition.tone} label={`${row.definition.label} ${periodLabel[period]}`} data-id="element-153" data-mp="tetltiufhjvq" />
              {showCoaching && row.pace === 'behind' ? <p className="mt-2 rounded-lg bg-warn-50 px-3 py-2 text-xs text-warn-700" data-id="element-154" data-mp="2eusmrx1jjucr">
                  {row.definition.coachingNote}
                </p> : null}
            </li>;
      })}
      </ul>
    </section>;
}